import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import AuthContext from "../store/AuthContext";
import type { IFilters } from "../interfaces/Components/ITable";
import { ITablePreference, ITableFiltersOnly } from "../interfaces/models/IUserPreferences";
import { useUserPreferences } from "./useUserPreferences";
import { useLocalPreferences } from "./useLocalPreferences";

const PATCH_DEBOUNCE_MS = 1000;
function omitIdDeep(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(omitIdDeep);
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const { id: _omit, ...rest } = o;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) {
      out[k] = omitIdDeep(v);
    }
    return out;
  }
  return value;
}

function sanitizeFiltersForPersistence(
  filters: Record<string, IFilters>,
): Record<string, IFilters> {
  const out: Record<string, IFilters> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (!v || typeof v !== "object") {
      out[k] = v;
      continue;
    }
    out[k] = {
      ...v,
      value: omitIdDeep(v.value) as IFilters["value"],
    };
  }
  return out;
}

function sanitizeInputFiltersForPersistence(
  inputFilters: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(inputFilters)) {
    out[k] = omitIdDeep(v);
  }
  return out;
}

const API_TABLE_KEYS = ["columnOrder", "columnVisibility", "columnWidths", "pageSize", "sorting"] as const;
const LOCAL_TABLE_KEYS = ["filters", "inputFilters"] as const;

function pickKeys<T extends Record<string, unknown>>(
  obj: T,
  keys: readonly string[]
): Partial<T> {
  const out: Partial<T> = {};
  for (const k of keys) {
    if (obj[k] !== undefined) {
      (out as Record<string, unknown>)[k] = obj[k];
    }
  }
  return out;
}

export function useTablePreferences(tableKey: string | null) {
  const { isAuthenticated } = useContext(AuthContext);
  const { userPreferences, patchUserPreferences, loading } = useUserPreferences();
  const { setTableFilters, userId, tableFilters } = useLocalPreferences();

  const fromApi = tableKey ? userPreferences?.tablePreferences?.[tableKey] : undefined;
  const tablePrefs: ITablePreference = useMemo(() => {
    if (!tableKey) {
      return {};
    }
    const fromLocal = tableFilters[tableKey] ?? {};
    return {
      ...fromApi,
      ...(fromLocal.filters !== undefined && { filters: fromLocal.filters }),
      ...(fromLocal.inputFilters !== undefined && { inputFilters: fromLocal.inputFilters }),
    };
  }, [tableKey, fromApi, tableFilters]);

  const pendingPartialRef = useRef<Partial<ITablePreference> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userPreferencesRef = useRef(userPreferences);
  userPreferencesRef.current = userPreferences;

  const flushPendingPatch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const pending = pendingPartialRef.current;
    pendingPartialRef.current = null;
    if (!tableKey || !pending || Object.keys(pending).length === 0) {
      return;
    }

    const apiPart = pickKeys(pending as Record<string, unknown>, API_TABLE_KEYS as unknown as string[]);
    const localPart = pickKeys(pending as Record<string, unknown>, LOCAL_TABLE_KEYS as unknown as string[]);

    const localSanitized: ITableFiltersOnly = { ...localPart } as ITableFiltersOnly;
    if (localSanitized.filters != null) {
      localSanitized.filters = sanitizeFiltersForPersistence(
        localSanitized.filters as Record<string, IFilters>,
      );
    }
    if (localSanitized.inputFilters != null) {
      localSanitized.inputFilters = sanitizeInputFiltersForPersistence(
        localSanitized.inputFilters as Record<string, unknown>,
      );
    }

    if (Object.keys(localSanitized).length > 0 && userId) {
      setTableFilters(tableKey, localSanitized);
    }
    if (Object.keys(apiPart).length > 0 && isAuthenticated) {
      const current = userPreferencesRef.current?.tablePreferences?.[tableKey] ?? {};
      const nextTable = { ...current, ...apiPart };
      const nextAll = {
        ...(userPreferencesRef.current?.tablePreferences ?? {}),
        [tableKey]: nextTable,
      };
      patchUserPreferences({ tablePreferences: nextAll }).catch(() => {});
    }
  }, [tableKey, userId, isAuthenticated, setTableFilters, patchUserPreferences]);

  const updateTablePrefs = useCallback(
    (partial: ITablePreference): Promise<void> => {
      if (!tableKey) {
        return Promise.resolve();
      }
      pendingPartialRef.current = {
        ...pendingPartialRef.current,
        ...partial,
      };
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(flushPendingPatch, PATCH_DEBOUNCE_MS);
      return Promise.resolve();
    },
    [tableKey, flushPendingPatch]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      flushPendingPatch();
    };
  }, [flushPendingPatch]);

  return {
    tablePrefs,
    updateTablePrefs,
    loading,
  };
}
